import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query/react";
import type { NavSection } from "@skypointcloud/skypoint-ui";
import {
  buildNavSections,
  inboxAllowedFor,
  needsPerformanceNavContext,
  usePerformanceNavContext,
} from "./sidebarConfig";
import { IRootReducerState } from "../../../store/IRootReducer";
import { useGetMyPermissionsQuery } from "../../../store/api/rbacApi";
import { useGetEngagementEnabledQuery } from "@/store/api/candidateEngagementApi";
import { useGetInboxCountsQuery } from "../../../store/api/emailApi";
import { getInstanceIdFromUrl } from "../../../utils/instanceId";
import { useLocationScope } from "../../../components/shared/scope";
import { useEnabledProducts } from "../../../hooks/useEnabledProducts";
import { useSharePointConnected } from "../../../hooks/useSharePointConnected";
import { useActiveProductState } from "../../../hooks/useActiveProduct";
import { skipsHireShellReads } from "../../../components/products/activeProduct";
import { useSkipsHireShellReads } from "../../../hooks/useSkipsHireShellReads";
import { DEFAULT_PRODUCT, type ProductKey } from "../../../../shared/products/keys";

export interface ShellNavSections {
  /** The navigation the shell's sidebar draws from, Settings entries included (as data). */
  sections: NavSection[];
  /** The shell's product (useActiveProductState). */
  activeProduct: ProductKey;
}

/**
 * The navigation of the shell the user is in: the sections the sidebar draws, built from the same
 * inputs as always. Moved out of sidebar.tsx unchanged so the tiled Settings pages and "Back to
 * Settings" read the very sections the sidebar draws (2026-10-08 shell spec, D5): one source.
 *
 * Which product's navigation is the active product (useActiveProduct): a product's own page forces
 * it, a shared page (Users, Account, ...) keeps the one the user is in. Nothing is built while it is
 * pending (products not resolved on a performance page, or on a shared page with a stored
 * performance choice), rather than flashing the hire sidebar there. The performance context loads
 * only for a product the instance has (a UI hint: the server gates).
 *
 * `inboxUnreadCount`: the sidebar's Inbox badge (useInboxUnreadCount). Only the sidebar passes it;
 * the count changes no gate, so the other readers build the same sections without asking for it.
 */
export function useShellNavSections({
  inboxUnreadCount,
  newDocumentSubmissions,
}: { inboxUnreadCount?: number; newDocumentSubmissions?: number } = {}): ShellNavSections {
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = authState.permissions;
  const isFullAccess = authState.isFullAccess;
  const instanceName = useSelector(
    (state: IRootReducerState) => state.instancesReducer.instancesData.selectedInstanceName,
  );

  // Fetch current user's RBAC navigation permissions (keyed by instanceId for proper caching)
  const instanceId = getInstanceIdFromUrl();
  const { data: myPerms } = useGetMyPermissionsQuery(instanceId ?? undefined);

  const { products, has, holdsRole } = useEnabledProducts();
  const { product: activeProduct, pending: productPending } = useActiveProductState();
  // In skyPerformance IT's shell for someone with no hire role, hire's reads are refused: not made.
  const skipHireReads = skipsHireShellReads(activeProduct, holdsRole(DEFAULT_PRODUCT));
  const dataScope = useLocationScope(skipHireReads ? "" : instanceId || "").scope?.dataScope;

  // Candidate Engagement is opt-in per instance; the nav entry stays hidden
  // until the tenant turns it on.
  const { data: engagementEnabled } = useGetEngagementEnabledQuery(undefined, {
    skip: skipHireReads,
  });

  // The Document Centre entry is drawn only once the organisation has
  // connected SharePoint: one cached status read, shared with the page itself.
  const { connected: isSharePointConnected } = useSharePointConnected(instanceId, !skipHireReads);

  // skyPerformance's context only in skyPerformance's shell: an IT shell makes none of its reads.
  const performance = usePerformanceNavContext(
    instanceId,
    activeProduct !== DEFAULT_PRODUCT &&
      needsPerformanceNavContext(activeProduct) &&
      has(activeProduct),
    myPerms?.permissions?.["users.manage"] === true,
  );

  // Build nav sections based on permissions and industry.
  const sections = productPending
    ? []
    : buildNavSections(
        {
          isFullAccess,
          isTenantAdmin,
          isPlatformAdmin,
          isInstanceAdmin,
          rbacNavigation: myPerms?.navigation,
          rbacPermissions: myPerms?.permissions,
          userRoleName: myPerms?.role ?? null,
          instanceName,
          instanceId,
          inboxUnreadCount,
          newDocumentSubmissions,
          dataScope,
          isSkyAgentEnabled: authState.capabilities.skyAgentEnabled,
          isSharePointConnected,
        },
        engagementEnabled ?? false,
        {
          activeProduct,
          enabledProducts: products,
          performance,
          performanceIt: { held: holdsRole(activeProduct) },
          hris: { held: holdsRole(activeProduct) },
        },
      );

  return { sections, activeProduct };
}

/**
 * The sidebar's Inbox badge. Polls candidate-inbox unread counts so the badge stays fresh. The args
 * match the Inbox page's (the header's Location, nothing until the scope loads), so the badge counts
 * what the Inbox lists and both share one cache entry and one poll. Nothing for a data scope the
 * server keeps out of the Inbox, or without inbox.view: either way it would only 403, every 30
 * seconds.
 */
export function useInboxUnreadCount(): number {
  const instanceId = getInstanceIdFromUrl();
  const { data: myPerms } = useGetMyPermissionsQuery(instanceId ?? undefined);
  const skipHireReads = useSkipsHireShellReads();
  const locationScope = useLocationScope(skipHireReads ? "" : instanceId || "");
  const dataScope = locationScope.scope?.dataScope;
  const { data: inboxCounts } = useGetInboxCountsQuery(
    instanceId &&
      locationScope.ready &&
      inboxAllowedFor(dataScope) &&
      myPerms?.permissions?.["inbox.view"] === true
      ? { instanceId, ...locationScope.params }
      : skipToken,
    { pollingInterval: 30_000, refetchOnFocus: true, skipPollingIfUnfocused: true },
  );
  return (inboxCounts?.email ?? 0) + (inboxCounts?.sms ?? 0);
}
